import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import {
  User, Mail, Phone, MapPin, Save, AlertCircle, CheckCircle, Camera, Upload, Lock
} from 'lucide-react';

const Profilo = ({ onSalvato }) => {
  const { utente, updateUser } = authStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [titolare, setTitolare] = useState(null);
  const [showCFWarning, setShowCFWarning] = useState(false);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoFile, setFotoFile] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    nome: '', cognome: '', codice_fiscale: '', email: '', telefono: '',
    telefono_secondario: '', indirizzo: '', citta: '', cap: '', provincia: '', scuola: ''
  });
  const [scuoleDisponibili, setScuoleDisponibili] = useState([]);

  useEffect(() => {
    if (utente?.titolare?.id) { caricaDati(); caricaScuole(); }
    else { setLoading(false); }
  }, [utente]);

  // Le scuole sono quelle servite dai gruppi: un elenco corto e già
  // scritto bene, meglio di un campo libero che genera dieci grafie
  // diverse dello stesso istituto.
  const caricaScuole = async () => {
    try {
      const res = await api.get('/trasporti/linee/');
      const gruppi = res.data.results || res.data || [];
      const mappa = new Map();
      gruppi.forEach((g) => {
        try {
          const elenco = JSON.parse(g.scuole_servite || '[]');
          if (Array.isArray(elenco)) {
            elenco.forEach((sc) => {
              const nome = (sc.denominazione || '').trim();
              if (nome && !mappa.has(nome)) mappa.set(nome, nome);
            });
          }
        } catch (e) { /* gruppo senza scuole configurate */ }
      });
      setScuoleDisponibili([...mappa.keys()].sort((a, b) => a.localeCompare(b)));
    } catch (error) {
      console.error('Errore caricamento scuole:', error);
    }
  };

  const caricaDati = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/anagrafiche/titolari/${utente.titolare.id}/`);
      const data = response.data;
      setTitolare(data);
      setFormData({
        nome: data.nome || '', cognome: data.cognome || '', codice_fiscale: data.codice_fiscale || '',
        email: data.email || '', telefono: data.telefono || '', telefono_secondario: data.telefono_secondario || '',
        indirizzo: data.indirizzo || '', citta: data.citta || '', cap: data.cap || '', provincia: data.provincia || '',
        scuola: data.scuola || ''
      });
      if (data.foto) setFotoPreview(data.foto);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      notify.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { notify.error('Il file non è un\'immagine'); return; }
    if (file.size > 5 * 1024 * 1024) { notify.error('Immagine troppo grande (max 5MB)'); return; }
    setFotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setFotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveFoto = () => {
    setFotoFile(null);
    setFotoPreview(titolare?.foto || null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.cognome) { notify.error('Nome e cognome sono obbligatori'); return; }
    if (!formData.email) { notify.error('Email è obbligatoria'); return; }
    if (!titolare?.codice_fiscale && formData.codice_fiscale) { setShowCFWarning(true); return; }
    await salvaDati();
  };

  const salvaDati = async () => {
    setSaving(true);
    const toastId = notify.loading('Salvataggio dati...');
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        // nome e cognome sono in sola lettura: li modifica solo l'ufficio.
        // Gli altri campi si inviano anche vuoti, altrimenti non si può
        // cancellare un dato inserito per errore.
        if (key !== 'nome' && key !== 'cognome') formDataToSend.append(key, formData[key] ?? '');
      });
      if (fotoFile) formDataToSend.append('foto', fotoFile);

      const response = await api.patch(`/anagrafiche/titolari/${utente.titolare.id}/`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setTitolare(response.data);
      setFormData({
        nome: response.data.nome || '', cognome: response.data.cognome || '',
        codice_fiscale: response.data.codice_fiscale || '', email: response.data.email || '',
        telefono: response.data.telefono || '', telefono_secondario: response.data.telefono_secondario || '',
        indirizzo: response.data.indirizzo || '', citta: response.data.citta || '',
        scuola: response.data.scuola || '',
        cap: response.data.cap || '', provincia: response.data.provincia || ''
      });
      if (response.data.foto) setFotoPreview(response.data.foto);
      setFotoFile(null);
      updateUser({ ...utente, titolare: response.data });
      notify.update(toastId, { type: 'success', render: 'Dati salvati con successo!' });
      setShowCFWarning(false);
      // Avvisa la pagina: se l'anagrafica è ora completa deve sbloccare
      // subito le altre sezioni, senza costringere a ricaricare.
      onSalvato?.();
    } catch (error) {
      console.error('Errore:', error);
      const dati = error.response?.data || {};
      const primoErrore = Object.values(dati).flat().find(v => typeof v === 'string');
      const errorMsg = dati.detail || primoErrore || 'Errore nel salvataggio';
      notify.update(toastId, { type: 'error', render: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const calcolaCompletamento = () => {
    const campiTotali = 10;
    let c = 0;
    if (formData.nome) c++;
    if (formData.cognome) c++;
    if (formData.codice_fiscale) c++;
    if (formData.email) c++;
    if (formData.telefono) c++;
    if (formData.indirizzo) c++;
    if (formData.citta) c++;
    if (formData.cap) c++;
    if (formData.provincia) c++;
    if (fotoPreview) c++;
    return Math.round((c / campiTotali) * 100);
  };

  const isVerificato = () => {
    return formData.codice_fiscale && formData.email && formData.indirizzo &&
      formData.citta && formData.cap && formData.provincia && fotoPreview;
  };

  if (loading) {
    return (
      <div className="du-loading">
        <div className="du-spinner"></div>
        <p>Caricamento profilo...</p>
      </div>
    );
  }

  if (!utente?.titolare?.id) {
    return (
      <div className="du-empty">
        <AlertCircle size={56} strokeWidth={1.5} />
        <h3>Errore</h3>
        <p>Impossibile caricare i dati del profilo</p>
      </div>
    );
  }

  // Conta solo la foto già salvata: quella appena scelta e non ancora
  // inviata deve poter essere sostituita prima del salvataggio.
  const fotoBloccata = Boolean(titolare?.foto);

  const completamento = calcolaCompletamento();
  const verificato = isVerificato();

  // Dati che mancano, calcolati dal server: sono gli stessi che rendono
  // l'abbonamento utilizzabile, quindi non vanno riscritti qui a mano.
  const mancanti = titolare?.dati_mancanti || [];

  return (
    <div className="du-profilo">
      {mancanti.length > 0 && (
        <div className="du-avviso-anagrafica">
          <AlertCircle size={20} />
          <div>
            <strong>Completa i tuoi dati per usare l'abbonamento</strong>
            <p>
              Finché mancano questi dati il tesserino non è valido a bordo:
              {' '}<b>{mancanti.join(', ').toLowerCase()}</b>.
            </p>
          </div>
        </div>
      )}

      {/* Modal CF */}
      {showCFWarning && (
        <div className="du-modal-overlay" onClick={() => setShowCFWarning(false)}>
          <div className="du-modal" onClick={(e) => e.stopPropagation()}>
            <div className="du-modal-head">
              <AlertCircle size={40} color="#f59e0b" />
              <h3>Attenzione!</h3>
            </div>
            <div className="du-modal-body">
              <p>Il <strong>Codice Fiscale</strong> non potrà più essere modificato dopo il salvataggio.</p>
              <p>Sei sicuro di voler continuare?</p>
            </div>
            <div className="du-modal-foot">
              <button className="du-modal-btn-cancel" onClick={() => setShowCFWarning(false)} disabled={saving}>Annulla</button>
              <button className="du-modal-btn-ok" onClick={salvaDati} disabled={saving}>{saving ? 'Salvataggio...' : 'Conferma'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="du-section-head">
        <h2>Il Mio Profilo</h2>
        <p>Completa i tuoi dati per verificare l'account</p>
      </div>

      {/* Verifica */}
      <div className={`du-verifica ${verificato ? 'ok' : 'ko'}`}>
        <div className="du-verifica-head">
          {verificato ? <CheckCircle size={22} /> : <AlertCircle size={22} />}
          <div>
            <h3>{verificato ? 'Account Verificato' : 'Account Non Verificato'}</h3>
            <p>{verificato ? 'Tutti i dati necessari sono stati inseriti' : 'Completa i campi obbligatori per verificare l\'account'}</p>
          </div>
        </div>
        <div className="du-progress">
          <div className="du-progress-fill" style={{ width: `${completamento}%` }}></div>
        </div>
        <p className="du-progress-text">Completamento: {completamento}%</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="du-form">
        {/* Dati Personali */}
        <div className="du-form-section">
          <h3 className="du-form-title"><User size={18} /> Dati Personali</h3>
          <div className="du-form-row">
            <div className="du-form-group">
              <label>Nome *</label>
              <input type="text" name="nome" value={formData.nome} onChange={handleChange} required disabled style={{ background: '#f8fafc', cursor: 'not-allowed' }} />
              <span className="du-field-hint">Non modificabile</span>
            </div>
            <div className="du-form-group">
              <label>Cognome *</label>
              <input type="text" name="cognome" value={formData.cognome} onChange={handleChange} required disabled style={{ background: '#f8fafc', cursor: 'not-allowed' }} />
              <span className="du-field-hint">Non modificabile</span>
            </div>
          </div>
          <div className="du-form-group">
            <label>
              Codice Fiscale
              {!formData.codice_fiscale && <span className="du-req-badge">Richiesto per verifica</span>}
            </label>
            <input
              type="text" name="codice_fiscale" value={formData.codice_fiscale} onChange={handleChange}
              maxLength={16} placeholder="RSSMRA80A01H501U"
              disabled={saving || !!titolare?.codice_fiscale}
              style={titolare?.codice_fiscale ? { background: '#f8fafc', cursor: 'not-allowed' } : {}}
            />
            {titolare?.codice_fiscale ? (
              <span className="du-field-hint">Non modificabile dopo l'inserimento</span>
            ) : (
              <span className="du-field-hint warn">
                <AlertCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />
                Non potrà essere modificato dopo il salvataggio
              </span>
            )}
          </div>
        </div>

        {/* Foto */}
        <div className="du-form-section">
          <h3 className="du-form-title">
            <Camera size={18} /> Foto Profilo
            {!fotoPreview && <span className="du-req-badge">Richiesta per verifica</span>}
          </h3>
          <div className="du-foto-container">
            <div className="du-foto-preview">
              {fotoPreview ? (
                <>
                  <img src={fotoPreview} alt="Foto profilo" />
                  {fotoFile && (
                    <button type="button" className="du-foto-remove" onClick={handleRemoveFoto} disabled={saving}>Rimuovi</button>
                  )}
                </>
              ) : (
                <div className="du-foto-placeholder">
                  <User size={48} strokeWidth={1.5} />
                  <p>Nessuna foto</p>
                </div>
              )}
            </div>
            <div className="du-foto-info">
              {fotoBloccata ? (
                /* Una volta caricata la foto non si sostituisce: serve a
                   riconoscere chi sale, e cambiarla vanificherebbe il
                   controllo dell'autista. */
                <div className="du-foto-bloccata">
                  <Lock size={14} />
                  <div>
                    <strong>Foto già caricata</strong>
                    <p>Per sostituirla contatta l'ufficio allo 0823 741 491.</p>
                  </div>
                </div>
              ) : (
                <>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFotoChange} style={{ display: 'none' }} disabled={saving} />
                  <button type="button" className="du-foto-btn" onClick={() => fileInputRef.current?.click()} disabled={saving}>
                    <Upload size={16} /> Carica Foto
                  </button>
                </>
              )}
              <div className="du-foto-reqs">
                <p className="du-foto-reqs-title"><AlertCircle size={12} /> Requisiti Foto:</p>
                <ul>
                  <li>Volto ben visibile e riconoscibile</li>
                  <li>Foto recente e a colori</li>
                  <li>Sfondo neutro (preferibilmente chiaro)</li>
                  <li>Formato: JPG, PNG (max 5MB)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Contatti */}
        <div className="du-form-section">
          <h3 className="du-form-title"><Mail size={18} /> Contatti</h3>
          <div className="du-form-group">
            <label>Email *</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={saving} />
          </div>
          <div className="du-form-row">
            <div className="du-form-group">
              <label>Telefono</label>
              <input type="tel" name="telefono" value={formData.telefono} onChange={handleChange} disabled={saving} />
            </div>
            <div className="du-form-group">
              <label>Tel. Secondario</label>
              <input type="tel" name="telefono_secondario" value={formData.telefono_secondario} onChange={handleChange} disabled={saving} />
            </div>
          </div>
        </div>

        {/* Indirizzo */}
        <div className="du-form-section">
          <h3 className="du-form-title"><MapPin size={18} /> Indirizzo</h3>
          <div className="du-form-group">
            <label>
              Via/Piazza
              {!formData.indirizzo && <span className="du-req-badge">Richiesto per verifica</span>}
            </label>
            <input type="text" name="indirizzo" value={formData.indirizzo} onChange={handleChange} placeholder="Via Roma 123" disabled={saving} />
          </div>
          <div className="du-form-row">
            <div className="du-form-group" style={{ flex: 2 }}>
              <label>Città {!formData.citta && <span className="du-req-badge">Richiesto</span>}</label>
              <input type="text" name="citta" value={formData.citta} onChange={handleChange} disabled={saving} />
            </div>
            <div className="du-form-group">
              <label>CAP {!formData.cap && <span className="du-req-badge">Richiesto</span>}</label>
              <input type="text" name="cap" value={formData.cap} onChange={handleChange} maxLength={5} placeholder="00100" disabled={saving} />
            </div>
            <div className="du-form-group">
              <label>Provincia</label>
              <input type="text" name="provincia" value={formData.provincia} onChange={handleChange} maxLength={2} placeholder="RM" disabled={saving} />
            </div>
            <div className="du-form-group du-form-group-full">
              <label>Scuola {!formData.scuola && <span className="du-req-badge">Richiesto</span>}</label>
              <select name="scuola" value={formData.scuola} onChange={handleChange} disabled={saving}>
                <option value="">— seleziona la scuola —</option>
                {scuoleDisponibili.map((sc) => (
                  <option key={sc} value={sc}>{sc}</option>
                ))}
                {/* Se la scuola registrata non è fra quelle servite non
                    va persa: resta selezionata e visibile */}
                {formData.scuola && !scuoleDisponibili.includes(formData.scuola) && (
                  <option value={formData.scuola}>{formData.scuola}</option>
                )}
              </select>
              {scuoleDisponibili.length === 0 && (
                <small className="du-hint">
                  Elenco non disponibile: contatta l'ufficio allo 0823 741 491
                </small>
              )}
            </div>
          </div>
        </div>

        {/* Submit */}
        <button type="submit" className="du-btn-save" disabled={saving}>
          <Save size={18} />
          <span>{saving ? 'Salvataggio...' : 'Salva Modifiche'}</span>
        </button>
        <p className="du-form-hint">* Campi obbligatori</p>
      </form>
    </div>
  );
};

export default Profilo;
